import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "@/components/ui/sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const getToken = () => localStorage.getItem("admin_token");
const authHeaders = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` });

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [editingProject, setEditingProject] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const emptyProject = { name: "", segment: "", location: "", summary: "", tags: "", images: [], featured: false, order: 0 };
  const [form, setForm] = useState(emptyProject);

  const fetchData = useCallback(async () => {
    if (!getToken()) { navigate("/admin"); return; }
    try {
      const [pRes, cRes] = await Promise.all([
        fetch(`${API}/api/projects`, { headers: authHeaders() }),
        fetch(`${API}/api/admin/contacts`, { headers: authHeaders() }),
      ]);
      if (pRes.ok) setProjects(await pRes.json());
      if (cRes.ok) setContacts(await cRes.json());
      else if (cRes.status === 401) { localStorage.clear(); navigate("/admin"); }
    } catch { toast.error("Erro ao carregar dados"); }
  }, [navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const logout = () => { localStorage.clear(); navigate("/admin"); };

  const openNew = () => { setEditingProject(null); setForm(emptyProject); setShowForm(true); };
  const openEdit = (p) => {
    setEditingProject(p.id);
    setForm({ name: p.name, segment: p.segment, location: p.location, summary: p.summary, tags: p.tags.join(", "), images: [...p.images], featured: p.featured, order: p.order });
    setShowForm(true);
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    const newUrls = [];
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      try {
        const res = await fetch(`${API}/api/admin/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${getToken()}` },
          body: formData,
        });
        if (!res.ok) { const err = await res.json(); throw new Error(err.detail || "Erro"); }
        const data = await res.json();
        newUrls.push(`${API}${data.url}`);
      } catch (err) {
        toast.error(`Erro ao enviar ${file.name}: ${err.message}`);
      }
    }
    if (newUrls.length > 0) {
      setForm(f => ({ ...f, images: [...f.images, ...newUrls] }));
      toast.success(`${newUrls.length} imagem(ns) enviada(s)!`);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeImage = (idx) => {
    setForm(f => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };

  const saveProject = async (e) => {
    e.preventDefault();
    const body = {
      name: form.name, segment: form.segment, location: form.location, summary: form.summary,
      tags: form.tags.split(",").map(t => t.trim()).filter(Boolean),
      images: form.images,
      featured: form.featured, order: Number(form.order),
    };
    try {
      const url = editingProject ? `${API}/api/admin/projects/${editingProject}` : `${API}/api/admin/projects`;
      const method = editingProject ? "PUT" : "POST";
      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(body) });
      if (!res.ok) throw new Error();
      toast.success(editingProject ? "Projeto atualizado!" : "Projeto criado!");
      setShowForm(false);
      fetchData();
    } catch { toast.error("Erro ao salvar projeto"); }
  };

  const deleteProject = async (id) => {
    if (!window.confirm("Excluir este projeto?")) return;
    try {
      await fetch(`${API}/api/admin/projects/${id}`, { method: "DELETE", headers: authHeaders() });
      toast.success("Projeto excluído");
      fetchData();
    } catch { toast.error("Erro ao excluir"); }
  };

  const labelStyle = { fontFamily: "'DM Mono', monospace", fontSize: "0.6rem", letterSpacing: "0.16em", color: "var(--muted2)", textTransform: "uppercase", display: "block", marginBottom: 4 };
  const tabStyle = (active) => ({
    fontFamily: "'DM Mono', monospace", fontSize: "0.68rem", letterSpacing: "0.12em", textTransform: "uppercase",
    padding: "8px 16px", background: active ? "var(--gold)" : "transparent", color: active ? "#09090b" : "var(--muted)",
    border: active ? "none" : "1px solid var(--border)", borderRadius: 4, cursor: "pointer", textDecoration: "none",
  });

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", color: "var(--text)" }}>
      {/* Header */}
      <header style={{ borderBottom: "1px solid var(--border)", background: "var(--surface)", padding: "0 24px" }}>
        <div style={{ maxWidth: 1140, margin: "0 auto", height: 56, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Link to="/" style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: "1rem", color: "var(--text)", textDecoration: "none" }}>
            BIM<span style={{ color: "var(--gold)" }}>tec</span>
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.58rem", color: "var(--muted2)", marginLeft: 10, letterSpacing: "0.1em", textTransform: "uppercase" }}>Admin</span>
          </Link>
          <button onClick={logout} style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.62rem", letterSpacing: "0.12em", textTransform: "uppercase", background: "none", border: "1px solid var(--border)", borderRadius: 4, padding: "6px 14px", color: "var(--muted)", cursor: "pointer" }} data-testid="admin-logout-button">
            Sair
          </button>
        </div>
      </header>

      <div style={{ maxWidth: 1140, margin: "0 auto", padding: "32px 24px" }}>
        {/* Info box */}
        <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 6, padding: "14px 20px", marginBottom: 24, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#22c55e", display: "inline-block" }} />
          <p style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.62rem", color: "var(--muted)", letterSpacing: "0.06em" }}>
            Painel administrativo · Acesse pelo rodapé do site ou diretamente em <strong style={{ color: "var(--gold)" }}>/admin</strong>
          </p>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 8, marginBottom: 32 }}>
          <button onClick={() => setTab("projects")} style={tabStyle(tab === "projects")} data-testid="tab-projects">Projetos ({projects.length})</button>
          <button onClick={() => setTab("contacts")} style={tabStyle(tab === "contacts")} data-testid="tab-contacts">Mensagens ({contacts.length})</button>
        </div>

        {/* PROJECTS TAB */}
        {tab === "projects" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <h2 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 600, fontSize: "1.2rem" }}>Gerenciar Projetos</h2>
              <button onClick={openNew} className="btn-gold" data-testid="new-project-button" style={{ fontSize: "0.65rem", padding: "8px 16px" }}>
                + Novo Projeto
              </button>
            </div>

            {showForm && (
              <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: 24, marginBottom: 24 }}>
                <h3 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 600, fontSize: "0.95rem", marginBottom: 20 }}>
                  {editingProject ? "Editar Projeto" : "Novo Projeto"}
                </h3>
                <form onSubmit={saveProject} data-testid="project-form" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div><label style={labelStyle}>Nome</label><input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="cb-input" required data-testid="project-name-input" /></div>
                  <div><label style={labelStyle}>Segmento</label><input value={form.segment} onChange={e => setForm(f => ({...f, segment: e.target.value}))} className="cb-input" required /></div>
                  <div><label style={labelStyle}>Localização</label><input value={form.location} onChange={e => setForm(f => ({...f, location: e.target.value}))} className="cb-input" required /></div>
                  <div><label style={labelStyle}>Ordem</label><input type="number" value={form.order} onChange={e => setForm(f => ({...f, order: e.target.value}))} className="cb-input" /></div>
                  <div style={{ gridColumn: "1 / -1" }}><label style={labelStyle}>Resumo</label><textarea value={form.summary} onChange={e => setForm(f => ({...f, summary: e.target.value}))} className="cb-input" rows={3} required /></div>
                  <div><label style={labelStyle}>Tags (separadas por vírgula)</label><input value={form.tags} onChange={e => setForm(f => ({...f, tags: e.target.value}))} className="cb-input" placeholder="BIM, Arquitetura" /></div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 20 }}>
                    <input type="checkbox" checked={form.featured} onChange={e => setForm(f => ({...f, featured: e.target.checked}))} id="featured" />
                    <label htmlFor="featured" style={{ ...labelStyle, marginBottom: 0 }}>Destaque na Home</label>
                  </div>

                  {/* Image upload section */}
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={labelStyle}>Imagens do projeto</label>
                    <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
                      <label style={{
                        display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 16px",
                        background: "var(--surface2)", border: "1px dashed var(--border2)", borderRadius: 6,
                        cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: "0.62rem",
                        color: "var(--gold)", letterSpacing: "0.1em", textTransform: "uppercase",
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>
                        {uploading ? "Enviando..." : "Enviar imagens"}
                        <input ref={fileInputRef} type="file" multiple accept="image/*" onChange={handleUpload} style={{ display: "none" }} data-testid="image-upload-input" disabled={uploading} />
                      </label>
                      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.58rem", color: "var(--muted2)" }}>
                        JPG, PNG, WebP · Máx. 10MB cada
                      </span>
                    </div>
                    {form.images.length > 0 && (
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
                        {form.images.map((url, idx) => (
                          <div key={idx} style={{ position: "relative", aspectRatio: "16/10", borderRadius: 4, overflow: "hidden", border: "1px solid var(--border)", background: "var(--surface2)" }}>
                            <img src={url} alt={`Imagem ${idx + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            <button type="button" onClick={() => removeImage(idx)} style={{
                              position: "absolute", top: 4, right: 4, width: 22, height: 22, borderRadius: "50%",
                              background: "rgba(239,68,68,0.9)", border: "none", color: "#fff", cursor: "pointer",
                              display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.7rem", fontWeight: 700,
                            }} data-testid={`remove-image-${idx}`}>×</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div style={{ gridColumn: "1 / -1", display: "flex", gap: 12 }}>
                    <button type="submit" className="btn-gold" data-testid="save-project-button" style={{ fontSize: "0.65rem", padding: "8px 20px" }}>Salvar</button>
                    <button type="button" onClick={() => setShowForm(false)} className="btn-ghost" style={{ fontSize: "0.65rem", padding: "8px 20px" }}>Cancelar</button>
                  </div>
                </form>
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 1, background: "var(--border)" }}>
              {projects.map((p) => (
                <div key={p.id} style={{ background: "var(--bg)", padding: "20px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                  <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 16 }}>
                    {p.images && p.images.length > 0 && (
                      <img src={p.images[0]} alt="" style={{ width: 48, height: 36, objectFit: "cover", borderRadius: 4, border: "1px solid var(--border)" }} />
                    )}
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                        <span style={{ fontFamily: "'Syne', sans-serif", fontWeight: 600, fontSize: "0.9rem", color: "var(--text)" }}>{p.name}</span>
                        {p.featured && <span style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.55rem", padding: "2px 6px", background: "var(--gold)", color: "#09090b", borderRadius: 3, letterSpacing: "0.08em", textTransform: "uppercase" }}>destaque</span>}
                      </div>
                      <p style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.6rem", color: "var(--muted2)", letterSpacing: "0.08em" }}>
                        {p.segment} · {p.location} · {p.images?.length || 0} img · ordem {p.order}
                      </p>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => openEdit(p)} style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.6rem", padding: "4px 12px", background: "none", border: "1px solid var(--border)", borderRadius: 4, color: "var(--gold)", cursor: "pointer", letterSpacing: "0.08em", textTransform: "uppercase" }}>Editar</button>
                    <button onClick={() => deleteProject(p.id)} style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.6rem", padding: "4px 12px", background: "none", border: "1px solid var(--border)", borderRadius: 4, color: "#ef4444", cursor: "pointer", letterSpacing: "0.08em", textTransform: "uppercase" }}>Excluir</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CONTACTS TAB */}
        {tab === "contacts" && (
          <div>
            <h2 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 600, fontSize: "1.2rem", marginBottom: 24 }}>Mensagens Recebidas</h2>
            {contacts.length === 0 ? (
              <p style={{ color: "var(--muted2)", fontFamily: "'DM Mono', monospace", fontSize: "0.75rem" }}>Nenhuma mensagem recebida ainda.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 1, background: "var(--border)" }}>
                {contacts.map((c, i) => (
                  <div key={i} style={{ background: c.read ? "var(--bg)" : "var(--surface)", padding: "20px 24px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <span style={{ fontFamily: "'Syne', sans-serif", fontWeight: 600, fontSize: "0.9rem", color: "var(--text)" }}>{c.name}</span>
                      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.58rem", color: "var(--muted2)", letterSpacing: "0.08em" }}>
                        {c.created_at ? new Date(c.created_at).toLocaleDateString("pt-BR") : ""}
                      </span>
                    </div>
                    <a href={`mailto:${c.email}`} style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.65rem", color: "var(--gold)", textDecoration: "none", letterSpacing: "0.06em" }}>{c.email}</a>
                    <p style={{ fontSize: "0.85rem", color: "var(--muted)", lineHeight: 1.6, marginTop: 10 }}>{c.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
